import {
  all,
  allPass,
  always,
  anyPass,
  both,
  complement,
  contains,
  curry,
  defaultTo,
  drop,
  equals,
  head,
  ifElse,
  isEmpty,
  isNil,
  join,
  map,
  not,
  nth,
  partition,
  pathSatisfies,
  pipe,
  split,
  values,
} from 'ramda';
import { compact, contained, isFunction, isObj, isString } from 'ramda-adjunct';
import { MediaType } from '../../../constants/MediaType';
import { NameSpace } from '../../../constants/NameSpace';
import { PerfMark } from '../../../constants/PerfMark';
import { ExternalSource, MainSource, OtherSource, Source, SupportedSourcesList } from '../../../constants/Source';
import { WixMediaRoot } from '../../../constants/WixMediaRoot';
import { bi } from '../../../services/loggers/bi';
import { extractBiParams } from '../../bi';
import { getCiVersion } from '../../env/getCiVersion';
import { getSessionId } from '../../env/getSessionId/getSessionId';
import { getDurationBetweenMarks, mark } from '../../env/performance';
import { isStandalone } from '../../env/utils';
import { isExternal, isMediaFromWix, isSourceSupported } from '../../media-manager-sdk/sources';
import { getAcceptedMediaTypesByNameSpace, isMediaTypeNotProvided, isSecureMediaType } from '../../mediaType';
import { RouteBuilder, RouteMatcher } from '../../RouteHelpers';
import { deserializeStringToArray } from '../../string';
import { defaultMMGRSettings } from './defaultMMGRSettings';
import { TMMGRSettings } from './withMMGRSettings.types';
import { mediaManagerOpened } from '@wix/bi-logger-media-manager-g6/v2';

export const dontInvokeForTest = (callback) => {
  if (process.env.NODE_ENV !== 'test') {
    callback();
  }
};

const getPathSearchPart = pipe<string, string[], string>(split('?'), nth(1));
const splitPathOnChunks = pipe<string, string[], string, string[], string[]>(split('?'), head, split('/'), compact);

export function getLocalConfig() {
  if (!isStandalone) {
    return {};
  }

  try {
    return require('../../../../config.private');
  } catch (ex) {
    if (!ex.message.includes('ENOENT')) {
      throw ex;
    }
  }
}

export function validateIncomingSettings(settings) {
  if (!settings.siteMediaToken) {
    throw new Error('siteMediaToken param is missing');
  }

  if (RouteMatcher.isEmpty(settings.path || '')) {
    return;
  }

  if (!settings.origin) {
    throw new Error(`Bi 'origin' is not specified. Should be 'editor', 'adi', 'backoffice', etc`);
  }

  if (!settings.openSource) {
    throw new Error(`Bi 'openSource' is not specified. Should be 'stores', 'background', 'my_uploads', etc`);
  }

  const hasSecureAndNonSecureMediaTypes = ifElse(
    anyPass([isEmpty, isNil]),
    always(false),
    pipe(split(','), partition(isSecureMediaType), all(pipe(isEmpty, not))),
  )(settings.mediaType);
  if (hasSecureAndNonSecureMediaTypes) {
    throw new Error(`Both secure and non-secure media types have been passed. Please, use either of them`);
  }
}

export const trackMediaManagerOpened = ({
  isFirst,
  setFirst,
  mmgrSettings,
}: {
  isFirst: boolean;
  setFirst: (boolean) => void;
  mmgrSettings: TMMGRSettings;
}) => {
  if (RouteMatcher.isEmpty(mmgrSettings.path || '')) {
    return;
  }

  mark(PerfMark.OpenEnd);

  const { publicMediaRoot, origin, openSource, metaSiteId } = extractBiParams(mmgrSettings);

  bi.logger().report(
    mediaManagerOpened({
      loadingTime: Math.round(getDurationBetweenMarks(PerfMark.OpenStart, PerfMark.OpenEnd)),
      isFirst,
      msid: metaSiteId,
      origin,
      source: openSource,
      context: isString(publicMediaRoot) ? publicMediaRoot : join(',', publicMediaRoot),
      sessionId: getSessionId(),
      mmgrVersion: getCiVersion(),
    }),
  );

  isFunction(setFirst) && setFirst(false);
};

export const propsAreValid = allPass([pathSatisfies(isString, ['mmgrSettings', 'siteMediaToken'])]);

export const processSource = (source: string): MainSource => {
  if (equals(source, OtherSource.Photostock)) {
    return MainSource.Shutterstock;
  }

  return source as MainSource;
};

const isSearchPathValid = (path: string) => {
  const pathChunks = splitPathOnChunks(path);

  if (!isSourceSupported(pathChunks[1])) {
    dontInvokeForTest(() => {
      console.error(
        `Source "${pathChunks[1]}" is invalid, please use one of the following ${join(',', SupportedSourcesList)}`,
      );
    });

    return false;
  }

  if (pathChunks.length < 3) {
    dontInvokeForTest(() => {
      console.error('Provide "searchQuery" to open Media Manager in search mode "search/:source/:searchQuery"');
    });

    return false;
  }

  return true;
};

const curriedBuildSearch = curry(RouteBuilder.Search);

const getSearchQuery = (path: string): string => {
  try {
    const queryParams = new URLSearchParams(path.split('?')[1]);
    return queryParams.get('searchQuery');
  } catch (error) {
    return '';
  }
};

export const parseSettingsPath = (
  path: string,
  tabsList: MainSource[] = null,
  publicMediaRoot: WixMediaRoot = WixMediaRoot.None,
): string => {
  if (RouteMatcher.isEmpty(path)) {
    return RouteBuilder.Empty();
  }

  const searchQuery = getSearchQuery(path);
  const [probablySource, ...pathChunks] = splitPathOnChunks(path);

  if (RouteMatcher.isSearch(path)) {
    if (isSearchPathValid(path)) {
      /*
        this is necessary, because currently our
        logic is not designed for searching in categories

        TODO: hopefully fix this in the future:
        rewrite splitPathOnChunks(),parseSettingsPath(),
        isSearchPathValid(), curriedBuildSearch(), RouteBuilder.Search
      */
      if (searchQuery) {
        return RouteBuilder.Search(pathChunks[0] as Source, searchQuery, pathChunks[1]);
      }
      return pipe<string[], string, string>(
        RouteMatcher.isPublicSearch(path)
          ? pipe<string[], string[], string>(drop(2), join('.'))
          : pipe<string[], string, string>(nth(2), defaultTo('')),
        curriedBuildSearch(nth(0, pathChunks as [MainSource, ...any[]]), nth(1, pathChunks)),
      )(pathChunks);
    }

    dontInvokeForTest(() => {
      console.error('Search path is not valid. You will be redirected to /private.');
    });

    return RouteBuilder.Source(MainSource.SiteFiles);
  }

  if (isExternal(probablySource as OtherSource)) {
    return RouteBuilder.ExternalSource(head(pathChunks) as ExternalSource);
  }

  const source = processSource(probablySource);

  if (!isSourceSupported(source)) {
    dontInvokeForTest(() => {
      console.error(
        `"${source}" source from "path" parameter is not supported! You will be redirected to private media.`,
      );
    });

    return RouteBuilder.Source(MainSource.SiteFiles);
  }

  if (isMediaFromWix(source)) {
    if (!publicMediaRoot) {
      throw new Error('You must to provide publicMediaRoot param with public file path');
    }

    return RouteBuilder.Source(source, ifElse(isEmpty, always(''), join('.'))(pathChunks));
  }

  if (tabsList && !contains(source, tabsList)) {
    throw new Error('You must include corresponding tab into tabsList to use this path');
  }

  return RouteBuilder.Source(source, pathChunks[0], pathChunks[1], getPathSearchPart(path));
};

type TIsSecureContext = (namespace: NameSpace) => boolean;
export const isSecureContext: TIsSecureContext = both(complement(equals(NameSpace.None)), contained(values(NameSpace)));

export const parseTabsList = map<string, MainSource>((tab) => {
  switch (tab) {
    case OtherSource.Photostock:
      return MainSource.Shutterstock;

    default:
      return tab as MainSource;
  }
});

export const parseMediaType = (namespace: NameSpace, mediaType: string) =>
  isSecureContext(namespace)
    ? getAcceptedMediaTypesByNameSpace(namespace)
    : isMediaTypeNotProvided(mediaType)
    ? defaultMMGRSettings.mediaType
    : mediaType;

export const prepareInitialMediaTypes = (mediaType: string, initialMediaTypes: MediaType[]): MediaType[] => {
  const mediaTypes = deserializeStringToArray<MediaType>(mediaType);
  const canUseMediaTypeInstead = isEmpty(initialMediaTypes) && mediaTypes.length === 1;

  return canUseMediaTypeInstead ? mediaTypes : initialMediaTypes;
};

export const parseMaybeObject = (maybeObject: string | {}) => {
  try {
    return isObj(maybeObject) ? maybeObject : JSON.parse(maybeObject as string);
  } catch {
    return {};
  }
};

export const toSecureUserBasedMediaUrl = (privateMediaRestAPI: string): string => `${privateMediaRestAPI}/secure-image`;

export const toSecureSiteBasedMediaUrl = (privateMediaRestAPI: string): string =>
  `${privateMediaRestAPI}/site/media/files/secure`;

export const selectTabsListForNamespace = (namespace: NameSpace): MainSource[] => {
  switch (namespace) {
    case NameSpace.WixMusic:
    case NameSpace.WixVideo:
    case NameSpace.ArtStore:
    case NameSpace.DigitalProduct:
    case NameSpace.All:
      return [MainSource.SiteFiles, MainSource.Trash];

    default:
      return [];
  }
};
